import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useTheme,
} from '@mui/material';
import { ExpandMoreRounded, LocalHospitalRounded } from '@mui/icons-material';
import { format } from 'date-fns';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import SectionCard from 'components/dashboard/chartmind/SectionCard';
import { isHospitalizationInstruction } from 'components/dashboard/chartmind/dischargeHospitalization';
import { useAuth } from 'hooks/useAuth';
import { db } from 'services/firebase';

// Mirrors the category ordering + accents used on the provider's DischargeStep
// so a delivered instruction set reads the same on both sides.
const CATEGORY_ORDER = [
  'hospitalization',
  'medication_general',
  'medication_specific',
  'activity',
  'diet',
  'followup',
  'warning_signs',
  'wound_care',
  'other',
];

const useCategoryColors = () => {
  const theme = useTheme();
  return {
    hospitalization: theme.palette.secondary.main,
    medication_general: theme.palette.primary.main,
    medication_specific: theme.palette.primary.main,
    activity: theme.palette.info.main,
    diet: theme.palette.success.main,
    followup: theme.palette.secondary.main,
    warning_signs: theme.palette.error.main,
    wound_care: theme.palette.warning.main,
    other: theme.palette.grey[600],
  };
};

const formatTimestamp = (createdAt) => {
  const date = createdAt?.toDate ? createdAt.toDate() : null;
  if (!date) return '';
  return format(date, "MMM d, yyyy 'at' h:mm a");
};

const DischargeDelivery = ({ delivery, defaultExpanded }) => {
  const colors = useCategoryColors();

  const orderedInstructions = useMemo(() => {
    const items = Array.isArray(delivery.instructions) ? delivery.instructions : [];
    const hospitalization = items.filter(isHospitalizationInstruction);
    const source = hospitalization.length ? hospitalization : items;
    return [...source].sort(
      (a, b) =>
        CATEGORY_ORDER.indexOf(a.category || 'other') -
        CATEGORY_ORDER.indexOf(b.category || 'other'),
    );
  }, [delivery.instructions]);

  const fromLabel = [delivery.providerName, delivery.organizationName]
    .filter(Boolean)
    .join(' • ');

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Stack spacing={0.25} sx={{ width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Discharge Instructions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[fromLabel, formatTimestamp(delivery.createdAt)].filter(Boolean).join(' — ')}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {orderedInstructions.map((inst, idx) => (
          <SectionCard
            key={`${inst.title}-${idx}`}
            title={inst.medicationName ? `${inst.title} — ${inst.medicationName}` : inst.title}
            content={inst.prose}
            accentColor={colors[inst.category] || colors.other}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const DischargeInstructionsPage = () => {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    const unsubscribe = db
      .collection('users')
      .doc(user.uid)
      .collection('dischargeInstructions')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snap) => {
          setDeliveries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (err) => {
          console.error('[DischargeInstructionsPage] subscription failed:', err);
          setError('Could not load your discharge instructions. Please try again.');
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [user?.uid]);

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Discharge Instructions"
        subtitle="Discharge instructions sent to you by your provider appear here."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : deliveries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <LocalHospitalRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            You have no discharge instructions yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            When a provider sends you instructions after a visit, they will show up here.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {deliveries.map((delivery, idx) => (
            <DischargeDelivery
              key={delivery.id}
              delivery={delivery}
              defaultExpanded={idx === 0}
            />
          ))}
        </Stack>
      )}
    </div>
  );
};

export default DischargeInstructionsPage;
